import React from 'react';
import { Card } from '../ui/card';
import { BarChart3, CheckCircle, FileText, Settings, TrendingUp } from 'lucide-react';

const ProjectSummary = ({ projects, departmentStats, projectSummary, onStatusClick }) => {
  // Use IWMS project summary data with new stage breakdown structure
  const summaryData = projectSummary || {
    total_projects: projects.length,
    proposed_projects: 0,
    work_order_projects: 0,
    completed_projects: projects.filter(p => p.status === 'Completed').length,
    remaining_projects: 0,
    pending_completion_projects: 0,
    completion_percentage: projects.length > 0 ? ((projects.filter(p => p.status === 'Completed').length / projects.length) * 100).toFixed(1) : 0,
    total_sanctioned_amount: projects.reduce((sum, p) => sum + (p.ts_amount || 0), 0),
    proposed_amount: 0,
    work_order_amount: 0,
    completed_amount: 0,
    remaining_amount: 0,
    pending_completion_amount: 0
  };

  // Extract values from summary data
  const totalProjects = summaryData.total_projects || 0;
  const proposedProjects = summaryData.proposed_projects || 0;
  const workOrderProjects = summaryData.work_order_projects || 0;
  const completedProjects = summaryData.completed_projects || 0;
  const remainingProjects = summaryData.remaining_projects || 0;
  const pendingCompletionProjects = summaryData.pending_completion_projects || 0;
  const completionRate = summaryData.completion_percentage || 0;
  const totalAmount = summaryData.total_sanctioned_amount || 0;
  const proposedAmount = summaryData.proposed_amount || 0;
  const workOrderAmount = summaryData.work_order_amount || 0;
  const completedAmount = summaryData.completed_amount || 0;
  const remainingAmount = summaryData.remaining_amount || 0;
  const pendingCompletionAmount = summaryData.pending_completion_amount || 0;

  // Format currency helper
  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  return (
    <Card className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between">
        {/* Total Projects */}
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl">
            <BarChart3 className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-3xl font-bold text-slate-800">{totalProjects}</div>
            <div className="text-sm font-medium text-slate-600">Total Projects</div>
            <div className="text-xs font-semibold text-slate-500">{formatCurrency(totalAmount)}</div>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="h-12 w-px bg-slate-300 mx-4"></div>

        {/* Proposed Projects - Clickable */}
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-all duration-200"
          onClick={() => onStatusClick('Proposed')}
          title="Click to view proposed projects"
        >
          <FileText className="h-5 w-5 text-blue-600" />
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{proposedProjects}</div>
            <div className="text-xs text-blue-600 mb-1">Proposed</div>
            <div className="text-xs font-semibold text-blue-500">{formatCurrency(proposedAmount)}</div>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="h-12 w-px bg-slate-300 mx-4"></div>

        {/* Work Order Projects - Clickable */}
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:bg-orange-50 p-2 rounded-lg transition-all duration-200"
          onClick={() => onStatusClick('Work Order')}
          title="Click to view work order projects"
        >
          <Settings className="h-5 w-5 text-orange-600" />
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{workOrderProjects}</div>
            <div className="text-xs text-orange-600 mb-1">Work Order</div>
            <div className="text-xs font-semibold text-orange-500">{formatCurrency(workOrderAmount)}</div>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="h-12 w-px bg-slate-300 mx-4"></div>

        {/* Completed Projects - Clickable */}
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:bg-green-50 p-2 rounded-lg transition-all duration-200"
          onClick={() => onStatusClick('Completed')}
          title="Click to view completed projects"
        >
          <CheckCircle className="h-5 w-5 text-green-600" />
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{completedProjects}</div>
            <div className="text-xs text-green-600 mb-1">Completed</div>
            <div className="text-xs font-semibold text-green-500">{formatCurrency(completedAmount)}</div>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="h-12 w-px bg-slate-300 mx-4"></div>

        {/* Remaining Projects - Clickable */}
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-all duration-200"
          onClick={() => onStatusClick('Remaining')}
          title="Click to view remaining projects"
        >
          <TrendingUp className="h-5 w-5 text-slate-600" />
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-600">{remainingProjects}</div>
            <div className="text-xs text-slate-600 mb-1">Remaining</div>
            <div className="text-xs font-semibold text-slate-500">{formatCurrency(remainingAmount)}</div>
          </div>
        </div>

        {/* Vertical divider before completion */}
        <div className="h-12 w-px bg-slate-300 mx-4"></div>

        {/* Completion Percentage */}
        <div className="text-center">
          <div className="text-2xl font-bold text-teal-600">{completionRate}%</div>
          <div className="text-xs text-teal-600">Completion</div>
        </div>
      </div>
    </Card>
  );
};

export default ProjectSummary;